import React, { useEffect, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import api from "../api/axios";
import "../assets/styles/notificationSidebar.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const getIcon = (type) => {
  switch (type) {
    case "order":
      return "pi pi-shopping-bag";
    case "payment":
      return "pi pi-credit-card";
    case "refund":
      return "pi pi-refresh";
    case "invoice":
      return "pi pi-file";
    case "cart":
      return "pi pi-shopping-cart";
    case "customer":
      return "pi pi-user";
    case "system":
      return "pi pi-cog";
    case "dispute":
      return "pi pi-comments";
    default:
      return "pi pi-bell";
  }
};

const formatTime = (date) => {
  if (!date) return "";

  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);

  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} mins ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

const PAGE_NAME = "Notifications";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  // The Axios interceptor handles the global 403 popup.
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message || fallbackMessage
  );
};

const NotificationSidebar = ({ visible, onHide, onUnreadChange }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

const fetchNotifications = async () => {
  try {
    setLoading(true);

    const res = await api.get("/admin/notifications", {
      params: {
        limit: 8,
      },
      headers: getPermissionHeaders("view"),
    });

    const rows = Array.isArray(res.data?.data)
      ? res.data.data
      : [];

    const unread = Number(
      res.data?.unread_count || 0
    );

    setNotifications(rows);
    setUnreadCount(unread);
    onUnreadChange?.(unread);
  } catch (error) {
    showRequestError(
      error,
      "Failed to fetch notifications."
    );
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (visible) fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

const markAsRead = async (notificationId) => {
  try {
    await api.put(
      `/admin/notifications/${notificationId}/read`,
      {},
      {
        headers: getPermissionHeaders("edit"),
      }
    );

    await fetchNotifications();
  } catch (error) {
    showRequestError(
      error,
      "Failed to mark notification as read."
    );
  }
};

const markAllAsRead = async () => {
  try {
    await api.put(
      "/admin/notifications/mark-all-read",
      {},
      {
        headers: getPermissionHeaders("edit"),
      }
    );

    await fetchNotifications();
  } catch (error) {
    showRequestError(
      error,
      "Failed to mark all notifications as read."
    );
  }
};

const openNotification = async (notification) => {
  if (!notification.is_read) {
    await markAsRead(notification.notification_id);
  }

  if (notification.action_url) {
    onHide();
    navigate(String(notification.action_url).replace(/^\/admin/, ""));
  }
};

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={onHide}
      className="premium-notification-sidebar"
    >
      <div className="notification-panel">
        <div className="notification-panel-header">
          <div>
            <p>Admin Alerts</p>
            <h2>Notifications</h2>
            {/* <span>{unreadCount} unread updates</span> */}
          </div>

          <div className="notification-header-icon">
            <i className="pi pi-bell" />
          </div>
        </div>

        <div className="notification-summary">
          <div>
            <strong>{notifications.length}</strong>
            <span>Shown</span>
          </div>

          <div>
            <strong>{unreadCount}</strong>
            <span>Unread</span>
          </div>
        </div>

        <div className="notification-list">
          {loading ? (
            <div className="notification-loading">
              <i className="pi pi-spin pi-spinner" />
              <p>Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="empty-notification-state">
              <i className="pi pi-bell" />
              <h3>No notifications</h3>
              <p>You are all caught up.</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.notification_id}
                className={
                  notification.is_read
                    ? "notification-item"
                    : "notification-item unread"
                }
                onClick={() => openNotification(notification)}
              >
                <div className="notification-icon">
                  <i className={getIcon(notification.type)} />
                </div>

                <div className="notification-content">
                  <div>
                    <h3>{notification.title}</h3>
                    {!notification.is_read && <span>New</span>}
                  </div>

                  <p>{notification.message}</p>

                  {notification.metadata?.order_number && (
                    <small>Order: {notification.metadata.order_number}</small>
                  )}

                  <small>{formatTime(notification.createdAt)}</small>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="notification-footer">
          <button type="button" className="ghost-action bottonborder" onClick={onHide}>
            Close
          </button>

          <button
            type="button"
            className="primary-action"
            onClick={markAllAsRead}
          >
            Mark All Read
          </button>
        </div>
      </div>
    </Sidebar>
  );
};

export default NotificationSidebar;
